import { useFormContext, useFieldArray } from 'react-hook-form'
import {
  FormField,
  FormItem,
  FormControl,
  FormMessage,
  FormLabel,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { Plus, Trash2 } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { SectorFormValues } from './SectorSchema'

export const SolutionsTab = () => {
  const form = useFormContext<SectorFormValues>()

  const saas = useFieldArray({
    control: form.control,
    name: 'saas_examples',
  })

  const impact = useFieldArray({
    control: form.control,
    name: 'business_impact',
  })

  return (
    <div className="space-y-8">
      {/* SaaS Solutions */}
      <div className="space-y-4">
        <div className="flex items-center justify-between border-b pb-2">
          <h3 className="font-semibold text-sm uppercase tracking-wider text-slate-500">
            SaaS Applications
          </h3>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              saas.append({ name: '', description: '', benefit: '' })
            }
          >
            <Plus className="w-4 h-4 mr-2" /> Add App
          </Button>
        </div>

        <div className="grid gap-4">
          {saas.fields.map((field, index) => (
            <Card key={field.id}>
              <CardContent className="p-4 space-y-3 relative">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute top-2 right-2 text-red-500 h-6 w-6"
                  onClick={() => saas.remove(index)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name={`saas_examples.${index}.name`}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs">App Name</FormLabel>
                        <FormControl>
                          <Input placeholder="App Name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name={`saas_examples.${index}.benefit`}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs">Key Benefit</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="e.g. +20% Efficiency"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={form.control}
                  name={`saas_examples.${index}.description`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs">Description</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="What does it do?"
                          className="h-16"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Business Impact */}
      <div className="space-y-4">
        <div className="flex items-center justify-between border-b pb-2">
          <h3 className="font-semibold text-sm uppercase tracking-wider text-slate-500">
            Business Impact Metrics
          </h3>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => impact.append({ label: '', value: '' })}
          >
            <Plus className="w-4 h-4 mr-2" /> Add Metric
          </Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {impact.fields.map((field, index) => (
            <div
              key={field.id}
              className="flex gap-2 items-end p-3 border rounded-md"
            >
              <div className="flex-1 space-y-2">
                <FormField
                  control={form.control}
                  name={`business_impact.${index}.value`}
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          placeholder="Value (e.g. +30%)"
                          className="font-bold text-primary"
                          {...field}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name={`business_impact.${index}.label`}
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          placeholder="Label (e.g. Growth)"
                          className="text-xs"
                          {...field}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="text-red-500 h-8 w-8"
                onClick={() => impact.remove(index)}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
